// -----------------------------------------------------------
// FloraIQ - Manuais de uso do CTA e do coletor de campo
// -----------------------------------------------------------
// Texto curto, passo a passo, com captura de cada tela.
// Imagens em public/manual/ (capturas reais do sistema).

export interface ManualStep {
  /** Texto do passo; **negrito** destaca o nome do botao ou campo. */
  text: string;
}

export interface ManualSection {
  id: string;
  title: string;
  intro?: string;
  image?: { src: string; alt: string; mobile?: boolean };
  steps?: ManualStep[];
  tip?: string;
  warning?: string;
}

export interface Manual {
  slug: "cta" | "coletor";
  title: string;
  subtitle: string;
  backTo: string;
  backLabel: string;
  sections: ManualSection[];
}

const s = (text: string): ManualStep => ({ text });

export const MANUALS: Record<Manual["slug"], Manual> = {
  cta: {
    slug: "cta",
    title: "Manual do CTA Arbóreo",
    subtitle: "Como cadastrar, consultar e vistoriar os exemplares arbóreos pelo computador.",
    backTo: "/cta",
    backLabel: "Voltar ao CTA",
    sections: [
      {
        id: "lista",
        title: "1. A tela do CTA",
        intro:
          "É a lista de todos os exemplares arbóreos cadastrados. Abra pelo menu lateral, no ícone da árvore.",
        image: { src: "/manual/cta_01_lista.webp", alt: "Tela do CTA Arbóreo com a lista de exemplares" },
        steps: [
          s("Os quadros do topo mostram os números do cadastro: exemplares, vistorias validadas, vistorias em revisão, risco e espécies."),
          s("Use a **barra de busca** para achar um exemplar pelo código CTA, espécie, rua ou bairro. O botão **Filtros** refina por tipo, risco e situação."),
          s("Clique em **Ver FCA** para abrir a ficha completa do exemplar."),
          s("**Exportar** baixa a lista que está na tela em planilha (abre no Excel)."),
          s("**Coletor de campo** abre a versão para celular e tablet. **Abrir mapa** mostra os exemplares no mapa."),
          s("**Adicionar CTA** começa o cadastro de um exemplar novo."),
        ],
      },
      {
        id: "localizacao",
        title: "2. Cadastrar um exemplar — passo 1: localização",
        intro: "Clique em **Adicionar CTA**. O cadastro tem três passos e o primeiro é dizer onde está o tronco.",
        image: { src: "/manual/cta_02_novo_localizacao.webp", alt: "Passo de localização do cadastro com mapa" },
        steps: [
          s("Escolha como informar a posição: **Usar GPS** (no aparelho, em campo), **Selecionar no mapa**, **Informar coordenadas** (graus ou UTM) ou **Informar endereço** (rua, CEP ou inscrição do lote)."),
          s("**Arraste o marcador verde** até a posição exata do tronco. Use os botões **+** e **−** para aproximar e o seletor no canto (Híbrido, Satélite, Ruas) para trocar o fundo do mapa."),
          s("O sistema procura exemplares já cadastrados num raio de 15 m. Se aparecer algum, confira: se for o mesmo exemplar, **não cadastre de novo**; se for outro, marque a caixa **Verifiquei os exemplares próximos e confirmo que este é um indivíduo novo** para continuar."),
          s("Bairro, quadra, lote e rua são preenchidos sozinhos a partir da posição — não precisa digitar."),
          s("Clique em **Continuar**."),
        ],
        tip: "Quanto mais perto do tronco o marcador estiver, mais correta fica a quadra, o lote e a rua sugeridos.",
      },
      {
        id: "identificacao",
        title: "3. Cadastrar um exemplar — passo 2: identificação",
        image: { src: "/manual/cta_03_novo_identificacao.webp", alt: "Passo de identificação do cadastro" },
        steps: [
          s("Confira o **projeto** e escolha o **tipo** (árvore, palmeira...)."),
          s("Escolha a **espécie**. Se não souber, deixe **Não identificada** — ela pode ser definida depois, na vistoria."),
          s("Deixe o **Código CTA** em branco: o sistema gera o próximo código livre (ex.: CTA-1-000005)."),
          s("**Logradouro** e **Bairro** já vêm preenchidos. Corrija só se estiverem errados."),
          s("Se quiser, preencha a **referência** (ex.: em frente ao nº 500), a fonte da imagem usada e uma observação, e adicione uma **foto geral**."),
          s("Clique em **Continuar**."),
        ],
      },
      {
        id: "confirmacao",
        title: "4. Cadastrar um exemplar — passo 3: confirmação",
        image: { src: "/manual/cta_03b_novo_confirmacao.webp", alt: "Resumo do cadastro antes de gravar" },
        steps: [
          s("Revise o resumo. Para corrigir algo, clique em **Voltar**."),
          s("Clique em **Cadastrar exemplar**. A ficha do exemplar abre em seguida, já com o código gerado."),
        ],
        tip: "O cadastro feito no escritório fica com a posição \"não confirmada em campo\". Ela é confirmada na primeira vistoria.",
      },
      {
        id: "fca",
        title: "5. A ficha do exemplar (FCA)",
        intro: "É a página de cada exemplar: foto, situação atual, medidas, vistorias, fotos e histórico.",
        image: { src: "/manual/cta_04_fca_topo.webp", alt: "Ficha cadastral do exemplar (FCA)" },
        steps: [
          s("**Nova vistoria** abre a vistoria técnica do exemplar."),
          s("**Adicionar fotos** leva à aba de fotos para anexar novas imagens."),
          s("**Registrar foto principal** (ou **Trocar foto principal**) define a foto que identifica o exemplar na ficha e no PDF."),
          s("**Editar cadastro** corrige espécie, posição ou endereço. É obrigatório explicar o motivo, e a mudança fica no histórico."),
          s("**Abrir no mapa** mostra o exemplar no mapa. **Gerar PDF** cria a ficha para imprimir."),
          s("O quadro **Situação Atual do Exemplar** mostra a condição vinda da última vistoria **validada**. Sem vistoria validada, aparece \"Não avaliado\"."),
          s("As abas (Localização, Vistorias, Fotos, Histórico...) mostram os detalhes."),
        ],
      },
      {
        id: "vistoria",
        title: "6. Fazer a vistoria",
        intro: "Na ficha, clique em **Nova vistoria**. A vistoria é dividida em etapas numeradas.",
        image: { src: "/manual/cta_07_vistoria.webp", alt: "Tela da vistoria técnica com as etapas" },
        steps: [
          s("Siga as etapas na ordem: identificação, dendrometria (medidas), fitossanidade, biomecânica, conflitos, risco, parecer, evidências (fotos, vídeo, áudio), responsável e revisão."),
          s("Tudo é **salvo sozinho** enquanto você preenche (aparece \"Salvo às ...\" no canto). Pode parar e continuar depois."),
          s("Na última etapa, **Revisão**, envie a vistoria. Ela vai para um analista, que valida ou devolve com o motivo."),
        ],
        warning: "Quem fez a vistoria não pode validar a própria vistoria. A validação é feita por outra pessoa.",
      },
      {
        id: "pdf",
        title: "7. Gerar o PDF da ficha",
        image: { src: "/manual/cta_06_pdf.webp", alt: "Ficha do exemplar em formato de impressão" },
        steps: [
          s("Na ficha, clique em **Gerar PDF**. A ficha abre em formato A4."),
          s("Clique em **Imprimir / Salvar como PDF** e, no destino, escolha **Salvar como PDF**."),
          s("O PDF traz a foto principal, a localização, a relação com o lote, as vistorias, o histórico e todas as fotos registradas."),
          s("O **QR Code** no topo abre a ficha do exemplar no sistema (é preciso ter login)."),
        ],
        tip: "Se o PDF sair sem as fotos ou sem o QR Code, feche as abas do FloraIQ, abra de novo e gere outra vez — o navegador estava com a versão antiga.",
      },
    ],
  },

  coletor: {
    slug: "coletor",
    title: "Manual do Coletor de Campo",
    subtitle: "Como usar o celular ou tablet para cadastrar, fotografar e vistoriar exemplares em campo.",
    backTo: "/coletor",
    backLabel: "Voltar ao coletor",
    sections: [
      {
        id: "inicio",
        title: "1. Tela inicial",
        intro:
          "Abra app.floraiq.ammagyn.org.br/coletor no celular. Para usar como aplicativo, toque em **Instalar o FloraIQ neste aparelho** quando o botão aparecer (no Android, pelo Chrome).",
        image: { src: "/manual/col_01_inicio.webp", alt: "Tela inicial do coletor de campo", mobile: true },
        steps: [
          s("No topo, **Online** indica que há internet e **GPS OK** mostra a precisão da posição (ex.: ±4 m)."),
          s("Os botões grandes levam às tarefas: **Nova árvore**, **Fotos de exemplar**, **Ler QR Code**, **Exemplares próximos** e **Nova vistoria**."),
          s("Mais abaixo aparece a **missão ativa**: projeto, área, meta e quantos exemplares já foram registrados."),
          s("A barra de baixo (Início, Nova Árvore, QR Code, Próximos) leva a qualquer tela com um toque."),
        ],
        warning: "Por enquanto o coletor precisa de internet: tudo é gravado direto no servidor.",
      },
      {
        id: "nova-arvore",
        title: "2. Cadastrar uma árvore nova",
        image: { src: "/manual/col_03_nova_arvore.webp", alt: "Tela Nova árvore do coletor", mobile: true },
        steps: [
          s("Toque em **Nova árvore** e depois em **Abrir cadastro do exemplar**. A posição do GPS do aparelho já vai junto."),
          s("Fique ao lado do tronco antes de abrir, para a posição sair correta."),
        ],
      },
      {
        id: "cadastro",
        title: "3. Preencher o cadastro",
        image: { src: "/manual/col_08_cadastro_celular.webp", alt: "Cadastro do exemplar no celular", mobile: true },
        steps: [
          s("Toque em **Usar GPS** e espere a precisão aparecer. Se precisar, arraste o marcador até o tronco."),
          s("Se aparecer um exemplar já cadastrado por perto, confira se não é o mesmo antes de continuar."),
          s("No passo 2, escolha a espécie (ou deixe **Não identificada**) e tire a **foto geral**."),
          s("No passo 3, confira e toque em **Cadastrar exemplar**."),
        ],
        tip: "Bairro, quadra, lote e rua são preenchidos pela posição. Não precisa digitar.",
      },
      {
        id: "proximos",
        title: "4. Exemplares próximos",
        intro: "Mostra os exemplares já cadastrados perto de você, do mais perto para o mais longe.",
        image: { src: "/manual/col_05_proximos.webp", alt: "Lista de exemplares próximos", mobile: true },
        steps: [
          s("Escolha a distância de busca: **50 m**, **100 m**, **250 m** ou **500 m**."),
          s("**Abrir ficha** mostra a ficha completa do exemplar."),
          s("**Fotos** abre a tela para fotografar o exemplar."),
          s("**Nova vistoria** começa a vistoria técnica desse exemplar."),
        ],
      },
      {
        id: "qrcode",
        title: "5. Ler o QR Code da plaqueta",
        image: { src: "/manual/col_04_qrcode.webp", alt: "Leitura de QR Code", mobile: true },
        steps: [
          s("Toque em **Ler QR Code** e aponte a câmera para a plaqueta do exemplar."),
          s("Se a câmera não ler, digite o código da plaqueta (ex.: CTA-1-000001) e toque em **Buscar**."),
        ],
      },
      {
        id: "busca",
        title: "6. Fotografar um exemplar já cadastrado",
        image: { src: "/manual/col_06_busca_exemplar.webp", alt: "Busca de exemplar para fotografar", mobile: true },
        steps: [
          s("Toque em **Fotos de exemplar**."),
          s("Ache o exemplar pelo **código ou espécie**, pelo **QR Code** ou pelos **Próximos**, e toque no ícone da câmera."),
        ],
      },
      {
        id: "fotos",
        title: "7. Tirar as fotos",
        image: { src: "/manual/col_07_exemplar_fotos.webp", alt: "Tela de fotos do exemplar", mobile: true },
        steps: [
          s("**Foto principal**: é a que identifica o exemplar na ficha e no PDF. Toque em **Trocar** para substituir."),
          s("Para as demais fotos, escolha em **O que a foto mostra** (vista geral, copa, tronco, colo, raízes, defeito...)."),
          s("Toque em **Tirar foto** (abre a câmera) ou **Da galeria** (fotos já tiradas). Pode enviar várias de uma vez."),
          s("As fotos ficam numa vistoria em rascunho. Toque em **Completar e enviar a vistoria** quando for preencher o restante."),
          s("**Abrir ficha completa (FCA)** mostra a ficha do exemplar."),
        ],
        tip: "Fotografe com boa luz e mostre a árvore inteira na vista geral. A posição do GPS é gravada junto com cada foto.",
      },
      {
        id: "vistoria",
        title: "8. Fazer a vistoria",
        image: { src: "/manual/col_09_vistoria_celular.webp", alt: "Vistoria técnica no celular", mobile: true },
        steps: [
          s("Abra pela **Nova vistoria** dos próximos ou pela ficha do exemplar."),
          s("Marque **Localização confirmada em campo** quando estiver diante do exemplar."),
          s("Preencha as etapas numeradas. Tudo é salvo sozinho; dá para parar e continuar depois."),
          s("Na etapa **Revisão**, envie a vistoria para o analista."),
        ],
      },
      {
        id: "voltar",
        title: "9. Voltar ao CTA",
        steps: [
          s("Se você abriu o coletor pelo botão **Coletor de campo** do CTA, aparece o botão **← CTA** no topo para voltar."),
          s("Para ver este manual de novo, toque em **Como usar o coletor**, na tela inicial."),
        ],
      },
    ],
  },
};
